import * as React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Stack,
} from '@mui/material';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { List } from 'react-admin';
import { PageHeader } from '../../components/page-header/PageHeader';
import { BackButton } from '../../components/back-button/BackButton';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DeliveryService, DeliveryFeeResponse } from '../../services/delivery/deliveryService';
import * as Sentry from '@sentry/react';
import { PAGE_GUTTER } from '../../core/theme/layout';

const GOOGLE_MAPS_LIBRARIES: 'places'[] = ['places'];

const deliveryService = new DeliveryService();

interface AddressInputProps {
  label: string;
  placeholder: string;
  onPlaceChanged: (lat: number, lng: number, address: string) => void;
}

const AddressInput: React.FC<AddressInputProps> = ({ label, placeholder, onPlaceChanged }) => {
  const autocompleteRef = React.useRef<google.maps.places.Autocomplete | null>(null);

  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChangedInternal = () => {
    const place = autocompleteRef.current?.getPlace();
    if (place?.geometry?.location) {
      onPlaceChanged(
        place.geometry.location.lat(),
        place.geometry.location.lng(),
        place.formatted_address || '',
      );
    }
  };

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 0.5,
          fontWeight: 600,
          color: 'var(--color-text-primary)',
          fontSize: 'var(--text-sm)',
        }}
      >
        {label}
      </Typography>
      <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChangedInternal}>
        <input
          placeholder={placeholder}
          style={{
            width: '100%',
            // 44px minimum touch height; 16px text keeps iOS from zooming in
            // on focus (this is a raw input, so the theme cannot reach it).
            minHeight: 44,
            padding: '12px 16px',
            fontSize: '16px',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            outline: 'none',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--color-primary)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e0e0e0';
          }}
        />
      </Autocomplete>
    </Box>
  );
};

interface SelectedAddress {
  address: string;
  lat: number;
  lng: number;
}

export default function WoltCheckerPage(): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader back={<BackButton />} title={t('woltChecker.title')} />
      <List
        resource="woltChecker"
        title={t('resources.woltChecker')}
        actions={false}
        queryOptions={{ enabled: false }}
        pagination={false}
        sx={{
          '& .RaList-main': { px: PAGE_GUTTER },
          /* This page only borrows <List> as a shell, so strip the list chrome
             — otherwise its bordered white Paper showed up as an empty strip
             under the form card. */
          '& .RaList-content': {
            border: 'none !important',
            borderRadius: '0 !important',
            boxShadow: 'none',
            backgroundColor: 'transparent',
          },
        }}
      >
        <WoltCheckerLayout />
      </List>
    </>
  );
}

const WoltCheckerLayout: React.FC = () => {
  const { t } = useTranslation();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [pickup, setPickup] = React.useState<SelectedAddress | null>(null);
  const [dropoff, setDropoff] = React.useState<SelectedAddress | null>(null);
  const [isChecking, setIsChecking] = React.useState(false);
  const [result, setResult] = React.useState<DeliveryFeeResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const canCheck = Boolean(pickup && dropoff);

  const handleCheck = async () => {
    if (!pickup || !dropoff) return;
    setIsChecking(true);
    setResult(null);
    setError(null);
    try {
      const response = await deliveryService.getDeliveryFee({
        pickup: { location: { coordinates: { lat: pickup.lat, lon: pickup.lng } } },
        dropoff: { location: { coordinates: { lat: dropoff.lat, lon: dropoff.lng } } },
      });
      setResult(response);
    } catch (err: any) {
      Sentry.captureException(err);
      const status = err?.response?.status;
      setError(
        status === 400 || status === 422
          ? t('woltChecker.routeNotSupported')
          : t('woltChecker.checkFailed'),
      );
    } finally {
      setIsChecking(false);
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setPickup(null);
    setDropoff(null);
  };

  return (
    <Box sx={{ maxWidth: 600, pb: 'var(--space-8)' }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid var(--color-secondary)',
          borderRadius: 'var(--radius-xs)',
          p: 'var(--space-6)',
          mb: 'var(--space-6)',
        }}
      >
        <Stack spacing={1.5}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 'var(--space-8)' }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: 'var(--color-primary)',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {t('woltChecker.pickupLabel')}
              </Typography>
            </Box>
            {isLoaded ? (
              <AddressInput
                label={t('woltChecker.pickupAddress')}
                placeholder={t('woltChecker.searchAddress')}
                onPlaceChanged={(lat, lng, address) => setPickup({ lat, lng, address })}
              />
            ) : (
              <Box sx={{ height: 46, display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {pickup && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
              >
                📍 {pickup.address}
              </Typography>
            )}
          </Box>

          <Divider sx={{ borderStyle: 'dashed' }} />

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 'var(--space-8)' }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: '#f44336',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {t('woltChecker.dropoffLabel')}
              </Typography>
            </Box>
            {isLoaded ? (
              <AddressInput
                label={t('woltChecker.dropoffAddress')}
                placeholder={t('woltChecker.searchAddress')}
                onPlaceChanged={(lat, lng, address) => setDropoff({ lat, lng, address })}
              />
            ) : (
              <Box sx={{ height: 46, display: 'flex', alignItems: 'center' }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {dropoff && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}
              >
                📍 {dropoff.address}
              </Typography>
            )}
          </Box>

          {/* Phones: stack the actions and let them fill the width — a
              half-width CTA floating mid-card read as disabled. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'stretch',
              gap: 2,
              pt: 'var(--space-8)',
            }}
          >
            <Button
              variant="contained"
              onClick={handleCheck}
              disabled={!canCheck || isChecking}
              startIcon={
                isChecking ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <LocalShippingOutlinedIcon />
                )
              }
              sx={{
                bgcolor: 'var(--color-primary)',
                '&:hover': { bgcolor: 'var(--color-primary-hover)' },
                borderRadius: '8px',
                px: 'var(--space-6)',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {isChecking ? t('woltChecker.checking') : t('woltChecker.checkButton')}
            </Button>

            {(result || error) && (
              <Button
                variant="outlined"
                onClick={handleReset}
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
              >
                {t('woltChecker.reset')}
              </Button>
            )}
          </Box>
        </Stack>
      </Paper>

      {result && (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid var(--color-secondary)',
            borderRadius: 'var(--radius-xs)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              bgcolor: '#e8f5e9',
              px: 'var(--space-6)',
              py: 'var(--space-4)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <CheckCircleOutlineIcon sx={{ color: '#43a047', fontSize: 24 }} />
            <Typography sx={{ fontWeight: 700, color: '#2e7d32', fontSize: 'var(--text-md)' }}>
              {t('woltChecker.routeSupported')}
            </Typography>
          </Box>

          <Box sx={{ px: 'var(--space-6)', py: 'var(--space-6)' }}>
            <Stack
              direction="row"
              spacing={0}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Box
                sx={{
                  flex: 1,
                  pr: 'var(--space-6)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontWeight: 600,
                    }}
                  >
                    {t('woltChecker.deliveryCost')}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.75rem',
                    color: 'var(--color-primary)',
                  }}
                >
                  {result.amount.toFixed(2)} {result.currency}
                </Typography>
              </Box>

              {result.estimatedDeliveryTime != null && (
                <Box
                  sx={{
                    flex: 1,
                    pl: 'var(--space-6)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      {t('woltChecker.estimatedTime')}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.75rem', color: '#616161' }}>
                    ~{Math.round(result.estimatedDeliveryTime)} {t('woltChecker.minutes')}
                  </Typography>
                </Box>
              )}
            </Stack>

            {pickup && dropoff && (
              <Box
                sx={{
                  mt: 'var(--space-4)',
                  pt: 'var(--space-4)',
                  borderTop: '1px solid #f0f0f0',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  <strong>{t('woltChecker.from')}:</strong> {pickup.address}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
                >
                  <strong>{t('woltChecker.to')}:</strong> {dropoff.address}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {error && (
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon />}
          sx={{ borderRadius: 'var(--radius-xs)' }}
        >
          <Typography fontWeight={600}>{error}</Typography>
        </Alert>
      )}
    </Box>
  );
};
